import * as React from 'react'
import { X } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'

/**
 * CloseButton — the canonical dismiss affordance (the DS2026 modal "×"): an
 * icon-only `<button>` with a Phosphor `X`, gray-400 glyph that darkens to
 * gray-600 over a gray-100 hover background. Use it for modals/dialogs, drawers,
 * dismissible cards, banners, and removable chips/badges.
 *
 *   <CloseButton onClick={close} />                     // modal × (square)
 *   <CloseButton shape="round" size="xs" />             // chip / badge ×
 *
 * `shape` controls the hover background: `square` (rounded-md, the Figma modal
 * look — default, matches `PopoverClose`) or `round` (rounded-full, for round
 * surfaces like chips and badges). Always labelled (`aria-label` defaults to
 * "Close") and `type="button"` so it never submits a form. Position it at the
 * call site (e.g. `className="absolute right-3 top-3"`); the button is layout-
 * agnostic. Radix surfaces that own dismissal (Popover) keep their own `*Close`
 * wrapper — reach for this one for everything else.
 */

const sizeCls = {
  xs: 'size-4 [&>svg]:size-3',
  sm: 'size-6 [&>svg]:size-4',
  md: 'size-7 [&>svg]:size-5',
} as const

const shapeCls = {
  square: 'rounded-md',
  round: 'rounded-full',
} as const

const toneCls = {
  // Modal default — neutral gray glyph + gray hover background.
  default: 'text-fbui-gray-500 hover:bg-fbui-gray-100 hover:text-fbui-gray-600',
  // Inherit the parent's text color; the hover background is a faint tint of
  // that same color (color-mix), so a red badge gets a red ×/hover, etc.
  inherit:
    'text-current hover:text-current hover:bg-[color-mix(in_srgb,currentColor_14%,transparent)]',
} as const

export interface CloseButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: keyof typeof sizeCls
  /** Hover-background shape: `square` (modal, default) or `round` (chips/badges). */
  shape?: keyof typeof shapeCls
  /**
   * Glyph color: `default` (neutral gray) or `inherit` (matches the parent text
   * color — use inside a colored chip/badge so the × reads in that hue).
   */
  tone?: keyof typeof toneCls
}

export const CloseButton = React.forwardRef<
  HTMLButtonElement,
  CloseButtonProps
>(
  (
    {
      className,
      size = 'md',
      shape = 'square',
      tone = 'default',
      'aria-label': ariaLabel = 'Close',
      ...props
    },
    ref
  ) => (
    <button
      ref={ref}
      type="button"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex shrink-0 items-center justify-center outline-none transition-colors focus-visible:ring-2 focus-visible:ring-fbui-ring-brand disabled:pointer-events-none disabled:opacity-40',
        toneCls[tone],
        sizeCls[size],
        shapeCls[shape],
        className
      )}
      {...props}
    >
      <X weight="bold" aria-hidden="true" />
    </button>
  )
)
CloseButton.displayName = 'CloseButton'
